import React, { useState } from 'react';
import R from 'ramda';
import PropTypes from 'prop-types';
import {
  findSelectedFilterItem,
  generateButtonCrossIconSrc,
  generateFilterItemClassNames,
  generateFilterMainButtonClassNames,
  generateIcon,
  generateListClassNames,
} from './helpers';

const eqArrays = R.compose(R.isEmpty, R.symmetricDifference);

const mapFilterOptions = ({
  filterName,
  options,
  selectedFilters,
  onFilterClick,
}) =>
  R.map((option) => {
    const isSelected = !!findSelectedFilterItem({
      filterName,
      option,
      selectedFilters,
    });
    const optionColor = R.prop('color', option);

    return (
      <li>
        <button
          className={generateFilterItemClassNames({ isSelected, optionColor })}
          type="button"
          onClick={() => {
            onFilterClick({ filterName, option, isSelected });
          }}
        >
          {optionColor && (
            <span
              className="filter__filters-section-list-item-swatch"
              style={{
                backgroundColor: optionColor,
              }}
            />
          )}

          {R.prop('name', option)}
        </button>
      </li>
    );
  })(options);

const mapFilters = ({ filters, selectedFilters, onFilterClick }) =>
  R.map((filter) => (
    <div className="filter__filters-section">
      <h6 className="filter__filters-section-heading">
        {R.prop('name', filter)}
      </h6>

      <ul className="filter__filters-section-list">
        {mapFilterOptions({
          filterName: R.prop('name', filter),
          options: R.propOr([], 'options', filter),
          selectedFilters,
          onFilterClick,
        })}
      </ul>
    </div>
  ))(filters);

export const Filter = ({
  filterData,
  filters,
  selectedFilters = [],
  onApplyButtonClick,
  onResetButtonClick,
  onFiltersButtonClick,
  onFilterSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedFilters, setInternalSelectedFilters] = useState(
    selectedFilters
  );

  const isResetButtonDisabled =
    R.length(selectedFilters) === 0 && R.length(internalSelectedFilters) === 0;

  const onFilterClick = ({ filterName, option, isSelected }) => {
    if (onFilterSelect) {
      onFilterSelect({
        filterName,
        option,
        isSelected,
      });
    }

    if (!isSelected) {
      setInternalSelectedFilters([
        ...internalSelectedFilters,
        {
          categoryName: filterName,
          optionName: R.prop('name', option),
        },
      ]);
    } else {
      setInternalSelectedFilters(
        R.without(
          [
            {
              categoryName: filterName,
              optionName: R.prop('name', option),
            },
          ],
          internalSelectedFilters
        )
      );
    }
  };

  return (
    <div className="filter__wrapper">
      <div className="filter__container">
        {!isOpen && !isResetButtonDisabled && (
          <button
            type="button"
            className="filter__button filter__button-reset"
            disabled={isResetButtonDisabled}
            onClick={() => {
              if (onResetButtonClick) {
                setInternalSelectedFilters([]);
                onResetButtonClick();
              }
            }}
          >
            <img
              alt="filter button"
              className="filter__button-icon filter__button-reset-icon"
              src={generateButtonCrossIconSrc({
                disabled: isResetButtonDisabled,
              })}
            />

            {R.prop('resetButtonText', filterData)}
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            if (onFiltersButtonClick) {
              onFiltersButtonClick({ isOpen });
            }

            setIsOpen(!isOpen);
          }}
          className={generateFilterMainButtonClassNames({
            isOpen: isOpen || isResetButtonDisabled,
          })}
        >
          <img
            alt="filter button"
            className="filter__button-icon"
            src={generateIcon({ isOpen })}
          />
          {R.prop('buttonText', filterData)}
        </button>

        <div className={generateListClassNames({ isOpen })}>
          {mapFilters({
            filters,
            selectedFilters: internalSelectedFilters,
            onFilterClick,
          })}

          <div className="filter__controls">
            <button
              type="button"
              className="filter__button"
              disabled={
                R.length(selectedFilters) === 0 &&
                R.length(internalSelectedFilters) === 0
              }
              onClick={() => {
                if (onResetButtonClick) {
                  setInternalSelectedFilters([]);
                  onResetButtonClick();
                }
              }}
            >
              <img
                alt="filter button"
                className="filter__button-icon filter__button-reset-icon"
                src={generateButtonCrossIconSrc({
                  disabled: isResetButtonDisabled,
                })}
              />

              {R.prop('resetButtonText', filterData)}
            </button>

            <button
              type="button"
              className="filter__button"
              disabled={eqArrays(selectedFilters, internalSelectedFilters)}
              onClick={() => {
                if (onApplyButtonClick) {
                  onApplyButtonClick({
                    selectedFilters: internalSelectedFilters,
                  });
                  setIsOpen(false);
                }
              }}
            >
              {R.prop('applyButtonText', filterData)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

Filter.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          color: PropTypes.string,
          count: PropTypes.number,
          isSelected: PropTypes.bool,
          name: PropTypes.string.isRequired,
          uid: PropTypes.string,
        })
      ),
    })
  ),
  selectedFilters: PropTypes.arrayOf(
    PropTypes.shape({
      categoryName: PropTypes.string.isRequired,
      optionName: PropTypes.string.isRequired,
    })
  ),
  onApplyButtonClick: PropTypes.func,
  onResetButtonClick: PropTypes.func,
  onFiltersButtonClick: PropTypes.func,
  onFilterSelect: PropTypes.func,
  filterData: PropTypes.shape({
    buttonText: PropTypes.string.isRequired,
    applyButtonText: PropTypes.string.isRequired,
    resetButtonText: PropTypes.string.isRequired,
  }),
};
